import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isAuthenticated, clearTokens, getLogoutUrl } from '../services/auth';
import { api, ApiError } from '../services/api';
import type { User, Tenant } from '@traaker/shared';
import { applyUiLanguage } from '../i18n';
import { pushDataLayerEvent } from '../utils/analytics';
import {
  resolveInitialUiLanguage,
  normalizeUiLanguage,
  SIGNUP_LANG_STASH_KEY,
  type UiLanguage,
} from '../i18n/languages';

interface AuthState {
  loading: boolean;
  user: User | null;
  tenant: Tenant | null;
  needsOnboarding: boolean;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  /** Persist + apply the per-user UI language. Optimistic; survives reload via the user record. */
  setUiLanguage: (lang: UiLanguage) => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  loading: true,
  user: null,
  tenant: null,
  needsOnboarding: false,
  logout: () => {},
  refreshProfile: async () => {},
  setUiLanguage: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const loadProfile = async () => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const data = await api.get<{ user: User; tenant: Tenant }>('/users/me');
      setUser(data.user);
      setTenant(data.tenant);
      setNeedsOnboarding(false);

      // Fire the sign_up GTM event exactly once for a real account creation,
      // then consume the server-side marker so a later login/onboarding-resume
      // never re-fires it. Fire-and-forget — a consume failure just means a
      // (harmless) rare duplicate event on next login, never a missed one.
      if (data.user.firstLogin) {
        pushDataLayerEvent('sign_up');
        api.patch('/users/me/first-login-seen', {}).catch(() => {});
      }

      // Apply the UI language. If the user has a stored preference, use it;
      // otherwise fall back to the marketing-site signup language (if any),
      // then the browser language, AND persist it once so the choice sticks
      // across devices/reloads (first-login default).
      const stored = data.user.settings?.uiLanguage;
      if (stored) {
        applyUiLanguage(stored);
      } else {
        const initial = resolveInitialUiLanguage();
        applyUiLanguage(initial);
        // Best-effort persist; a failure just means we re-derive next login.
        api.patch('/users/me/settings', { uiLanguage: initial }).catch(() => {});
      }
      // Consumed (or irrelevant now that a preference exists) — clear so it
      // never leaks into a later, unrelated signup on the same browser.
      try {
        localStorage.removeItem(SIGNUP_LANG_STASH_KEY);
      } catch {
        // ignore
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // 401 = token valid (passed Cognito) but no user record in DynamoDB
        // → needs onboarding
        setNeedsOnboarding(true);
      } else {
        // Actual auth failure or network error → clear session
        clearTokens();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Safety net: a 402 TRIAL_EXPIRED from any call means the cached tenant is
  // stale (still 'trialing'). Re-fetch the profile so isTrialExpired flips and
  // the paywall overlay appears even if the snapshot lagged.
  useEffect(() => {
    const onExpired = () => loadProfile();
    window.addEventListener('traaker:trial-expired', onExpired);
    return () => window.removeEventListener('traaker:trial-expired', onExpired);
  }, []);

  const logout = () => {
    clearTokens();
    window.location.href = getLogoutUrl();
  };

  const setUiLanguage = async (lang: UiLanguage) => {
    const code = normalizeUiLanguage(lang);
    applyUiLanguage(code); // optimistic — flip the UI immediately
    setUser((prev) => (prev ? { ...prev, settings: { ...prev.settings, uiLanguage: code } } : prev));
    await api.patch('/users/me/settings', { uiLanguage: code });
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        user,
        tenant,
        needsOnboarding,
        logout,
        refreshProfile: loadProfile,
        setUiLanguage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
